import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Users, Mail, Building2, Inbox } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/leads");
        setLeads(data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const stats = [
    { icon: Inbox, label: "Total Leads", value: leads.length },
    { icon: Users, label: "New This View", value: leads.filter((l) => l.status === "new").length },
    { icon: Building2, label: "Account", value: (user?.role || "client").toUpperCase() },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center bg-ink text-white font-heading font-extrabold text-xs">OTB</span>
            <span className="font-heading font-bold text-ink">Client Portal</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slatey hidden sm:block" data-testid="dashboard-user-email">{user?.email}</span>
            <button onClick={handleLogout} data-testid="dashboard-logout"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-ink hover:text-ember transition-colors">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <h1 className="font-heading font-bold text-ink text-3xl">Welcome back, {user?.name || "Partner"}.</h1>
          <p className="mt-2 text-slatey">Here's the latest activity across your growth ecosystem.</p>
        </motion.div>

        <div className="mt-8 grid gap-6 sm:grid-cols-3">
          {stats.map((s) => (
            <div key={s.label} className="border border-slate-200 bg-white p-6" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
              <s.icon className="h-5 w-5 text-ember" />
              <div className="mt-4 font-heading font-extrabold text-ink text-3xl">{s.value}</div>
              <div className="text-sm text-slatey mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 border border-slate-200 bg-white">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
            <Mail className="h-4 w-4 text-ember" />
            <h2 className="font-heading font-bold text-ink">Inbound Leads</h2>
          </div>
          {loading ? (
            <div className="p-10 text-center text-slatey text-sm">Loading...</div>
          ) : leads.length === 0 ? (
            <div className="p-10 text-center text-slatey text-sm" data-testid="leads-empty">No leads yet. Submissions from the contact form will appear here.</div>
          ) : (
            <div className="divide-y divide-slate-100" data-testid="leads-list">
              {leads.map((l) => (
                <div key={l.lead_id} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <div className="font-semibold text-ink text-sm">{l.name} <span className="text-slatey font-normal">· {l.email}</span></div>
                    <div className="text-sm text-slatey mt-0.5">{l.message}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {l.interest && <span className="text-xs border border-slate-200 px-2.5 py-1 text-slatey">{l.interest}</span>}
                    <span className="text-xs uppercase tracking-wider text-ember font-semibold">{l.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
